import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { DISTRICTS, LOCAL_BODY_TYPES } from "@/data/static";
import { GRAMA_PANCHAYATS_BY_DISTRICT_BLOCK, BLOCK_PANCHAYATS_BY_DISTRICT, MUNICIPALITIES_BY_DISTRICT, CORPORATIONS_BY_DISTRICT, DISTRICT_PANCHAYATS_BY_DISTRICT } from "@/data/localBodies";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import DatePicker from "@/components/registers/DatePicker";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Check, ArrowLeft, ArrowRight, Crop } from "lucide-react";
import { toast } from "sonner";
import PhotoCropDialog from "@/components/PhotoCropDialog";

const STEPS = ["Personal", "Official", "Account", "Review"];

export default function Register() {
  const nav = useNavigate();
  const { register, user } = useAuth();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [cropSrc, setCropSrc] = useState(null); // dataURL of raw pick before cropping
  const [regState, setRegState] = useState(null);
  React.useEffect(() => {
    api.get("/settings/membership").then(r => setRegState(r.data)).catch(() => setRegState({ open: true }));
  }, []);
  const [data, setData] = useState({
    full_name: user?.full_name || "", gender: user?.gender || "Male", dob: user?.dob || "", address: user?.address || "", mobile: user?.mobile || "",
    photo_url: user?.photo_url || ("https://i.pravatar.cc/200?u=" + Math.random()),
    id_card_url: user?.id_card_url || "",
    employee_id: user?.employee_id || "", designation: user?.designation || "", department: user?.department || "Local Self Government Department",
    local_body_type: user?.local_body_type || "Grama Panchayat", local_body_name: user?.local_body_name || "", block_panchayat: user?.block_panchayat || "", district: user?.district || "Thiruvananthapuram",
    date_of_joining: user?.date_of_joining || "", years_of_service: user?.years_of_service || 0,
    educational_qualification: user?.educational_qualification || "", blood_group: user?.blood_group || "",
    has_service_break: user?.has_service_break || "No",
    previous_local_bodies: user?.previous_local_bodies || [],
    employment_type: user?.employment_type || "Contract",
    email: user?.email || "", password: "",
  });

  const set = (k, v) => setData(d => ({ ...d, [k]: v }));

  const validateStep = () => {
    if (step === 0) {
      if (!data.full_name || !data.dob || !data.address || !data.mobile) return "Please fill all personal details";
    }
    if (step === 1) {
      if (!data.employee_id || !data.designation) return "Please fill all official details";
      if (data.local_body_type === "Grama Panchayat" && !data.block_panchayat) return "Please select the Block Panchayat";
      if (!data.local_body_name) return data.local_body_type === "Grama Panchayat" ? "Please select the Grama Panchayat" : "Please select the Local Body";
      if (!data.date_of_joining) return "Please enter Date of Joining";
    }
    if (step === 2) {
      // If the user is already logged in with a password they don't need to set one again
      if (!data.email) return "Email is required";
      if (!user && data.password.length < 6) return "Password (min 6 chars) is required";
    }
    return null;
  };

  const next = () => {
    const err = validateStep();
    if (err) return setError(err);
    setError("");
    setStep(s => Math.min(s + 1, STEPS.length - 1));
  };

  const submit = async () => {
    setBusy(true); setError("");
    // For logged-in users who don't set a password, use a throwaway that
    // satisfies min_length. The backend's upgrade path only touches the
    // password_hash if the existing account has none (Google users).
    const password = data.password || (user ? Math.random().toString(36).slice(2, 12) + "!" : "");
    const r = await register({ ...data, password, years_of_service: Number(data.years_of_service) || 0 });
    setBusy(false);
    if (r.ok) {
      toast.success(user ? "Membership form saved!" : "Registered! Complete your payment to proceed.");
      nav("/dashboard");
    } else setError(r.error);
  };

  // Registration closed gate — allow logged-in office bearers, admin-created
  // legacy members, and already-paid/active members to complete their own details.
  const canCompleteExistingMembership = !!user && (
    user.role !== "member"
    || user.created_via === "admin_offline"
    || user.payment_status === "paid"
    || user.membership_status === "active"
    || user.membership_status === "pending_review"
    || !!user.membership_id
  );
  if (regState && regState.open === false && !canCompleteExistingMembership) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1 flex items-center justify-center px-4">
          <Card className="p-8 max-w-md text-center" data-testid="register-closed">
            <div className="text-xs uppercase tracking-widest text-brand-blue font-semibold">Registration Closed</div>
            <h1 className="text-2xl font-black tracking-tight mt-2">We&apos;re not accepting new members right now</h1>
            <p className="text-sm text-muted-foreground mt-3">{regState.closed_message || "Membership registration is currently closed."}</p>
            <div className="mt-5 flex justify-center gap-2">
              <Link to="/"><Button variant="outline">Back to Home</Button></Link>
              <Link to="/login"><Button className="bg-brand-blue hover:bg-brand-blue/90 text-white">Member Login</Button></Link>
            </div>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 max-w-3xl w-full mx-auto px-4 py-12">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <div className="mb-6">
            <div className="text-xs uppercase tracking-widest text-brand-blue font-semibold">{user?.created_via === "admin_offline" ? "Membership onboarding" : "Become a member"}</div>
            <h1 className="text-3xl sm:text-4xl font-black tracking-tight mt-1">{user?.created_via === "admin_offline" ? "Complete Your Membership" : "Join KELTA"}</h1>
            <p className="text-sm text-muted-foreground mt-2">
              {user?.created_via === "admin_offline"
                ? "Your account was created by your association office. Please review the prefilled details and complete the full membership form."
                : "Registration is a multi-step process: details, account, payment, admin review."}
            </p>
          </div>

          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              {STEPS.map((s, i) => (
                <div key={s} className={`flex items-center gap-2 text-sm ${i <= step ? "text-foreground" : "text-muted-foreground"}`}>
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                    i < step ? "bg-brand-green text-white"
                    : i === step ? "bg-brand-blue text-white"
                    : "bg-muted"
                  }`}>
                    {i < step ? <Check className="w-3.5 h-3.5" /> : i + 1}
                  </div>
                  <span className="hidden sm:inline">{s}</span>
                </div>
              ))}
            </div>
            <Progress value={((step + 1) / STEPS.length) * 100} className="h-1.5" />
          </div>

          <Card className="p-6 sm:p-8">
            {step === 0 && (
              <div className="space-y-4">
                <h2 className="font-bold text-xl">Personal Details</h2>
                <Field label="Full Name"><Input value={data.full_name} onChange={e => set("full_name", e.target.value)} data-testid="reg-full-name" /></Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Gender">
                    <Select value={data.gender} onValueChange={v => set("gender", v)}>
                      <SelectTrigger data-testid="reg-gender"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Male">Male</SelectItem>
                        <SelectItem value="Female">Female</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Date of Birth"><DatePicker value={data.dob} onChange={v => set("dob", v)} testid="reg-dob" /></Field>
                </div>
                <Field label="Mobile Number"><Input value={data.mobile} onChange={e => set("mobile", e.target.value)} placeholder="+91 ..." data-testid="reg-mobile" /></Field>
                <Field label="Address"><Textarea value={data.address} onChange={e => set("address", e.target.value)} data-testid="reg-address" /></Field>
                <Field label="Upload Photo">
                  <div className="flex items-center gap-3">
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        if (file.size > 5 * 1024 * 1024) {
                          setError("Photo must be smaller than 5MB");
                          e.target.value = "";
                          return;
                        }
                        const reader = new FileReader();
                        reader.onload = (ev) => setCropSrc(ev.target.result);
                        reader.readAsDataURL(file);
                        e.target.value = ""; // allow re-selecting the same file
                      }}
                      data-testid="reg-photo-upload"
                      className="flex-1"
                    />
                    {data.photo_url && (
                      <>
                        <img
                          src={data.photo_url}
                          alt="preview"
                          className="w-12 h-12 rounded-md object-cover ring-1 ring-border"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-9 px-2"
                          onClick={() => setCropSrc(data.photo_url)}
                          data-testid="reg-photo-recrop"
                          title="Re-crop"
                        >
                          <Crop className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">JPG/PNG, max 5MB. You&apos;ll be able to crop before it&apos;s saved.</p>
                </Field>
                <Field label="Photo URL (optional)"><Input value={data.photo_url} onChange={e => set("photo_url", e.target.value)} placeholder="https://..." data-testid="reg-photo" /></Field>
              </div>
            )}

            {step === 1 && (
              <div className="space-y-4">
                <h2 className="font-bold text-xl">Official Details</h2>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Employee ID"><Input value={data.employee_id} onChange={e => set("employee_id", e.target.value)} data-testid="reg-empid" /></Field>
                  <Field label="Designation"><Input value={data.designation} onChange={e => set("designation", e.target.value)} placeholder="Technical Assistant" data-testid="reg-designation" /></Field>
                </div>
                <Field label="Department"><Input value={data.department} onChange={e => set("department", e.target.value)} data-testid="reg-dept" /></Field>
                <Field label="District">
                  <Select value={data.district} onValueChange={v => setData(d => ({ ...d, district: v, block_panchayat: "", local_body_name: "" }))}>
                    <SelectTrigger data-testid="reg-district"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {DISTRICTS.map(d => <SelectItem key={d.slug} value={d.name}>{d.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Local Body Type">
                    <Select value={data.local_body_type} onValueChange={v => setData(d => ({ ...d, local_body_type: v, block_panchayat: "", local_body_name: "" }))}>
                      <SelectTrigger data-testid="reg-lbtype"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {LOCAL_BODY_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                  {data.local_body_type === "Grama Panchayat" ? (
                    <Field label="Block Panchayat">
                      <Select value={data.block_panchayat} onValueChange={v => setData(d => ({ ...d, block_panchayat: v, local_body_name: "" }))}>
                        <SelectTrigger data-testid="reg-block"><SelectValue placeholder="Select Block" /></SelectTrigger>
                        <SelectContent>
                          {(BLOCK_PANCHAYATS_BY_DISTRICT[data.district] || []).map(b => (
                            <SelectItem key={b} value={b}>{b}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                  ) : (
                    <Field label="Local Body Name">
                      {data.local_body_type === "Block Panchayat" ? (
                        <Select value={data.local_body_name} onValueChange={v => set("local_body_name", v)}>
                          <SelectTrigger data-testid="reg-lbname-select"><SelectValue placeholder="Select Block Panchayat" /></SelectTrigger>
                          <SelectContent>
                            {(BLOCK_PANCHAYATS_BY_DISTRICT[data.district] || []).map(b => (
                              <SelectItem key={b} value={b}>{b}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : data.local_body_type === "Municipality" ? (
                        <Select value={data.local_body_name} onValueChange={v => set("local_body_name", v)}>
                          <SelectTrigger data-testid="reg-lbname-select"><SelectValue placeholder="Select Municipality" /></SelectTrigger>
                          <SelectContent>
                            {(MUNICIPALITIES_BY_DISTRICT[data.district] || []).map(m => (
                              <SelectItem key={m} value={m}>{m}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : data.local_body_type === "Corporation" ? (
                        <Select value={data.local_body_name} onValueChange={v => set("local_body_name", v)}>
                          <SelectTrigger data-testid="reg-lbname-select"><SelectValue placeholder="Select Corporation" /></SelectTrigger>
                          <SelectContent>
                            {(CORPORATIONS_BY_DISTRICT[data.district] || []).map(c => (
                              <SelectItem key={c} value={c}>{c}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : data.local_body_type === "District Panchayat" ? (
                        <Select value={data.local_body_name} onValueChange={v => set("local_body_name", v)}>
                          <SelectTrigger data-testid="reg-lbname-select"><SelectValue placeholder="Select District Panchayat" /></SelectTrigger>
                          <SelectContent>
                            {(DISTRICT_PANCHAYATS_BY_DISTRICT[data.district] || []).map(dp => (
                              <SelectItem key={dp} value={dp}>{dp}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input value={data.local_body_name} onChange={e => set("local_body_name", e.target.value)} placeholder="e.g. Kochi Municipal Corporation" data-testid="reg-lbname" />
                      )}
                    </Field>
                  )}
                </div>
                {data.local_body_type === "Grama Panchayat" && (
                  <Field label="Grama Panchayat">
                    <Select
                      value={data.local_body_name}
                      onValueChange={v => set("local_body_name", v)}
                      disabled={!data.block_panchayat}
                    >
                      <SelectTrigger data-testid="reg-lbname"><SelectValue placeholder={data.block_panchayat ? "Select Grama Panchayat" : "Select Block Panchayat first"} /></SelectTrigger>
                      <SelectContent>
                        {((GRAMA_PANCHAYATS_BY_DISTRICT_BLOCK[data.district] || {})[data.block_panchayat] || []).map(gp => (
                          <SelectItem key={gp} value={gp}>{gp}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Date of Joining"><DatePicker value={data.date_of_joining} onChange={v => set("date_of_joining", v)} testid="reg-doj" /></Field>
                  <Field label="Years of Service"><Input type="number" min="0" value={data.years_of_service} onChange={e => set("years_of_service", e.target.value)} data-testid="reg-yos" /></Field>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Educational Qualification">
                    <Input value={data.educational_qualification} onChange={e => set("educational_qualification", e.target.value)} placeholder="e.g. Diploma in Civil Engineering" data-testid="reg-edu" />
                  </Field>
                  <Field label="Blood Group">
                    <Select value={data.blood_group} onValueChange={v => set("blood_group", v)}>
                      <SelectTrigger data-testid="reg-blood"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        {["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-", "Unknown"].map(b => (
                          <SelectItem key={b} value={b}>{b}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
                <Field label="Break in Service?">
                  <Select value={data.has_service_break} onValueChange={v => set("has_service_break", v)}>
                    <SelectTrigger data-testid="reg-break"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="No">No</SelectItem>
                      <SelectItem value="Yes">Yes</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                {data.has_service_break === "Yes" && (
                  <div className="p-3 rounded-md border border-border bg-muted/30 space-y-3" data-testid="reg-prev-lb-panel">
                    <div className="flex items-center justify-between">
                      <div className="text-xs uppercase tracking-wider font-semibold text-brand-blue">Previous Local Bodies</div>
                      <Button
                        type="button" size="sm" variant="outline"
                        data-testid="reg-add-prev-lb"
                        onClick={() => set("previous_local_bodies", [...data.previous_local_bodies, { name: "", type: "Grama Panchayat", from_date: "", to_date: "" }])}
                      >+ Add</Button>
                    </div>
                    {data.previous_local_bodies.length === 0 && (
                      <p className="text-xs text-muted-foreground">Click &quot;Add&quot; to enter each Local Body you previously worked in.</p>
                    )}
                    {data.previous_local_bodies.map((row, i) => (
                      <div key={i} className="grid grid-cols-2 gap-2" data-testid={`reg-prev-lb-row-${i}`}>
                        <Input placeholder="Local Body Name" value={row.name} onChange={e => {
                          const arr = [...data.previous_local_bodies]; arr[i].name = e.target.value; set("previous_local_bodies", arr);
                        }} />
                        <Select value={row.type} onValueChange={v => {
                          const arr = [...data.previous_local_bodies]; arr[i].type = v; set("previous_local_bodies", arr);
                        }}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {LOCAL_BODY_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <DatePicker value={row.from_date} onChange={v => {
                          const arr = [...data.previous_local_bodies]; arr[i].from_date = v; set("previous_local_bodies", arr);
                        }} />
                        <DatePicker value={row.to_date} onChange={v => {
                          const arr = [...data.previous_local_bodies]; arr[i].to_date = v; set("previous_local_bodies", arr);
                        }} />
                        <Button
                          type="button" size="sm" variant="ghost"
                          className="col-span-2 text-destructive hover:text-destructive h-8"
                          onClick={() => set("previous_local_bodies", data.previous_local_bodies.filter((_, x) => x !== i))}
                        >Remove</Button>
                      </div>
                    ))}
                  </div>
                )}
                {data.has_service_break === "No" && (
                  <Field label="Employment Type">
                    <Select value={data.employment_type} onValueChange={v => set("employment_type", v)}>
                      <SelectTrigger data-testid="reg-emptype"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Permanent">Permanent</SelectItem>
                        <SelectItem value="Contract">Contract</SelectItem>
                        <SelectItem value="Employment Exchange">Employment Exchange</SelectItem>
                        <SelectItem value="Deputation">Deputation</SelectItem>
                        <SelectItem value="Provisional">Provisional</SelectItem>
                        <SelectItem value="Daily Wage">Daily Wage</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                )}
              </div>
            )}

            {step === 2 && (
              <div className="space-y-4">
                <h2 className="font-bold text-xl">Account</h2>
                <Field label="Email"><Input type="email" value={data.email} onChange={e => set("email", e.target.value)} disabled={!!user} data-testid="reg-email" /></Field>
                {!user && (
                  <Field label="Password (min 6 chars)"><Input type="password" value={data.password} onChange={e => set("password", e.target.value)} data-testid="reg-password" /></Field>
                )}
                <p className="text-xs text-muted-foreground">
                  After registration: pay membership fee of ₹1,300, then an admin will review and activate your account.
                </p>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-4">
                <h2 className="font-bold text-xl">Review & Submit</h2>
                <div className="rounded-lg border border-border bg-muted/30 p-4 grid sm:grid-cols-2 gap-3 text-sm">
                  <Detail k="Name" v={data.full_name} />
                  <Detail k="Email" v={data.email} />
                  <Detail k="Mobile" v={data.mobile} />
                  <Detail k="Employee ID" v={data.employee_id} />
                  <Detail k="Designation" v={data.designation} />
                  <Detail k="District" v={data.district} />
                  <Detail k="Local Body" v={`${data.local_body_name} (${data.local_body_type})`} />
                  {data.local_body_type === "Grama Panchayat" && (
                    <Detail k="Block Panchayat" v={data.block_panchayat} />
                  )}
                  <Detail k="DOJ" v={data.date_of_joining} />
                  <Detail k="Qualification" v={data.educational_qualification || "—"} />
                  <Detail k="Blood Group" v={data.blood_group || "—"} />
                  <Detail k="Break in Service" v={data.has_service_break} />
                  {data.has_service_break === "Yes" ? (
                    <Detail k="Previous LBs" v={`${data.previous_local_bodies.length} entered`} />
                  ) : (
                    <Detail k="Employment Type" v={data.employment_type} />
                  )}
                </div>
                <div className="rounded-lg border border-brand-blue/30 bg-brand-blue/5 p-4 text-sm">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-semibold">Membership Fee</div>
                      <div className="text-xs text-muted-foreground">Annual fee, payable after registration</div>
                    </div>
                    <div className="text-2xl font-black text-brand-blue">₹1,300</div>
                  </div>
                </div>
              </div>
            )}

            {error && (
              <div className="mt-4 text-sm text-destructive bg-destructive/10 rounded-md p-2.5" data-testid="reg-error">
                {error}
              </div>
            )}

            <div className="mt-6 flex items-center justify-between">
              <Button
                variant="outline"
                onClick={() => setStep(s => Math.max(0, s - 1))}
                disabled={step === 0}
                data-testid="reg-back-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              {step < STEPS.length - 1 ? (
                <Button onClick={next} className="bg-brand-blue text-white hover:bg-brand-blue/90" data-testid="reg-next-btn">
                  Next <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button onClick={submit} disabled={busy} className="bg-brand-green text-white hover:bg-brand-green/90" data-testid="reg-submit-btn">
                  {busy ? "Submitting…" : user ? "Save Membership Details" : "Submit Registration"}
                </Button>
              )}
            </div>
          </Card>

          <div className="mt-4 text-center text-sm text-muted-foreground">
            Already a member? <Link to="/login" className="text-brand-blue font-medium">Login</Link>
          </div>
        </motion.div>
      </main>
      <Footer />
      <PhotoCropDialog
        open={!!cropSrc}
        imageSrc={cropSrc}
        onCancel={() => setCropSrc(null)}
        onCropped={(dataUrl) => { set("photo_url", dataUrl); setCropSrc(null); }}
      />
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}

function Detail({ k, v }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{k}</div>
      <div className="font-medium">{v || "—"}</div>
    </div>
  );
}
